<template>
    <div
        class="modal"
        v-if="showCreditScoreModal"
        aria-labelledby="edit-program-modal"
        role="dialog"
        aria-modal="true"
    >
        <div class="modal-overlay" aria-hidden="true"></div>
        <div
            class="inner shadow"
            style="-webkit-transform: translate3d(0, 0, 0)"
        >
            <div
                class="items-start p-6 flex flex-col lg:!flex-row justify-center bg-white dark:bg-slate-800 rounded-md"
            >
                <div class="ml-0 relative">
                    <div class="w-full">
                        <h3
                            class="text-slate-800 dark:text-slate-100 font-medium text-lg leading-7"
                            v-if="mode === 'create'"
                        >
                            Add Credit Score
                        </h3>
                        <h3
                            class="text-slate-800 dark:text-slate-100 font-medium text-lg leading-7"
                            v-if="mode === 'edit'"
                        >
                            Edit Credit Score
                        </h3>
                        <a
                            href="#"
                            class="flex p-2 items-start gap-2 absolute top-0 right-0 rounded text-slate-600 dark:text-slate-300 hover:text-gray-400"
                            role="button"
                            @click.prevent="closeModal()"
                            id="hs-credit-score-modal-close"
                        >
                            <Icon icon="close" />
                        </a>
                    </div>
                    <div
                        v-if="users.length > 1 && activeUser?.name"
                        class="flex items-center gap-2 mt-1 mb-2"
                    >
                        <div class="flex h-6 w-6 p-1 justify-center items-center rounded-full bg-slate-100 dark:bg-slate-700">
                            <Icon icon="user" class="w-4 h-4 text-slate-500 dark:text-slate-400" />
                        </div>
                        <span class="text-sm text-slate-800 dark:text-slate-100 leading-5">
                            {{ activeUser.name }}
                        </span>
                    </div>
                    <p class="mb-8 text-sm text-slate-500 dark:text-slate-400 font-normal leading-5">
                        Regularly update your credit score in My10x for the best recommendations
                    </p>

                    <div class="flex flex-col mb-2 gap-4">
                        <UiInput
                            id="creditscoredate"
                            name="score_date"
                            label="Date"
                            placeholder="Select Date ..."
                            :options="{
                                icon: 'calendar',
                            }"
                            :errors="errors"
                        >
                            <template #input>
                                <flat-pickr
                                    id="creditscoredate"
                                    :config="dateConfig"
                                    :class="['relative block w-full rounded py-1.5 pl-10 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:z-10 placeholder:text-sm focus:ring-0 active:ring-0 focus:border-slate-800 active:border-slate-800',
                                        dateError ? 'border-red-500 bg-white dark:bg-slate-800' : 'border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800']"
                                    name="score_date"
                                    v-model="scoreData.scoreDate"
                                    placeholder="Select Date ..."
                                ></flat-pickr>
                                <div v-if="dateError" class="text-xs text-red-500 font-medium flex items-center gap-1 mt-1">
                                    <Icon icon="caution" svgClass="w-3 h-3" />
                                    <span>Select date</span>
                                </div>
                            </template>
                        </UiInput>
                        <UiInput
                            id="score"
                            name="score"
                            label="FICO Score"
                            placeholder="Add Your Credit Score"
                            :options="{}"
                            :errors="errors"
                        >
                            <template #input>
                                <input
                                    type="number"
                                    name="score"
                                    v-model="scoreData.score"
                                    pattern="\d*"
                                    step="1"
                                    min="300"
                                    max="850"
                                    placeholder="Add Your Credit Score"
                                    @input="checkScoreLimits"
                                    class="relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:z-10 placeholder:text-sm focus:ring-0 active:ring-0 focus:border-slate-800 active:border-slate-800"
                                />
                                <span
                                    class="text-sm text-red-600"
                                    v-if="scoreMessage"
                                >Please enter a score between<br />300 and 850</span>
                            </template>
                        </UiInput>
                    </div>
                    <p class="mb-3">
                        <label class="mb-2 mr-3 flex flex-row">
                            <input
                                type="checkbox"
                                name="is_real_fico_score"
                                value="1"
                                class="form-checkbox mt-1"
                                v-model="scoreData.isRealFicoScore"
                            />
                            <span class="text-sm ml-2">
                                This is my real FICO score, not Vantage 3.0 or similar scores from Credit Karma or Credit Sesame. Learn
                                <a
                                    href="#"
                                    class="font-medium"
                                    style="color: var(--Primary-10x-Orange, #FD421D);"
                                    @click.prevent="toggleWhyMatter"
                                    id="hs-credit-score-why-matter"
                                    ref="whyMatterLink"
                                >
                                    Why does this matter?
                                </a>
                                Get your free real FICO score using
                                <a
                                    href="https://experianboost.com/"
                                    target="_blank"
                                    class="text-sm font-medium"
                                    style="color: var(--Primary-10x-Orange, #FD421D);"
                                    id="hs-credit-score-experian-boost-inline"
                                >
                                    Experian Boost
                                </a>.
                            </span>
                        </label>
                    </p>

                    <teleport to="body">
                        <div v-if="showWhyMatter" class="fixed inset-0 z-50" @click.self="showWhyMatter = false">
                            <div
                                class="flex w-120 max-w-lg p-6 flex-col items-start gap-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm absolute z-50"
                                :style="popoverStyle"
                                @click.stop
                            >
                                <div class="relative w-full">
                                    <h4 class="text-gray-800 dark:text-gray-100 font-medium text-base leading-6 mb-2">Why real FICO matters vs other scores</h4>
                                </div>
                                <div class="text-sm text-gray-500 dark:text-gray-400 leading-5">
                                    <p class="mb-3">
                                        Many "unofficial" credit scores provided by CreditKarma, Credit Sesame, your bank's free score, and similar services are known to be inaccurate and cannot be used to reliably track your credit score over time.
                                    </p>
                                    <p>
                                        Using an official source like
                                        <a
                                            href="https://experianboost.com/"
                                            target="_blank"
                                            class="font-medium"
                                            style="color: var(--Primary-10x-Orange, #FD421D);"
                                        >
                                            Experian Boost
                                        </a>
                                        ensures you're monitoring your "real" credit score - the same information most lenders use to evaluate your credit.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </teleport>


                    <Errors :errors="errors" />

                    <footer
                        class="flex justify-end items-start gap-2 self-stretch mt-8"
                    >
                        <button
                            type="button"
                            class="flex px-4 py-2.5 items-start gap-2 rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm"
                            @click.prevent="closeModal()"
                            id="hs-credit-score-cancel"
                        >
                            <span class="text-gray-800 dark:text-gray-100 font-medium text-sm leading-5">Cancel</span>
                        </button>
                        <button
                            v-if="mode === 'create'"
                            type="button"
                            class="flex px-4 py-2.5 justify-center items-center gap-2 rounded bg-gray-800 shadow-sm"
                            @click.prevent="submitCreditScore"
                            id="hs-credit-score-submit"
                        >
                            <span class="text-white font-medium text-sm leading-5">Add credit score</span>
                            <span
                                class="inline-flex ml-2"
                                v-if="loadingResponse"
                            >
                                <i class="fas fa-spinner-third fa-spin"></i>
                            </span>
                        </button>
                        <button
                            v-if="mode === 'edit'"
                            type="button"
                            class="flex px-4 py-2.5 justify-center items-center gap-2 rounded bg-gray-800 shadow-sm"
                            @click.prevent="updateScore"
                            id="hs-credit-score-update"
                        >
                            <span class="text-white font-medium text-sm leading-5">Save changes</span>
                            <span
                                class="inline-flex ml-2"
                                v-if="loadingResponse"
                            >
                                <i class="fas fa-spinner-third fa-spin"></i>
                            </span>
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCreditScoreStore } from '@/store/creditscore';
import { useAppStore } from '@/store/app';
import Errors from '@/components/errors.vue';
import FlatPickr from '@/components/ui/SafeFlatPickr.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    props: {},
    components: {
        FlatPickr,
        Errors,
        UiInput,
        Icon,
    },
    data: () => ({
        errors: [],
        dateError: false,
        scoreError: false,
        ficoCheckError: false,
        scoreData: {
            scoreDate: new Date(),
            score: '',
            isRealFicoScore: false,
            uuid: null,
        },
        scoreMessage: null,
        dateConfig: {
            dateFormat: 'm/d/Y',
            maxDate: 'today',
        },
        mode: 'create',
        showWhyMatter: false,
        whyMatterHoverTimeout: null,
        popoverStyle: {
            position: 'fixed',
            top: '0px',
            left: '0px',
            zIndex: 9999,
        },
    }),
    directives: {},
    computed: {
        ...mapState(useCreditScoreStore, [
            'loadingResponse',
            'showCreditScoreModal',
            'activeScore',
            'activeUser',
            'users',
        ]),
    },
    watch: {
        showCreditScoreModal(newVal, oldVal) {
            if (newVal) {
                if (this.activeScore) {
                    this.scoreData = {
                        scoreDate: this.activeScore.score_date,
                        score: this.activeScore.score,
                        isRealFicoScore: this.activeScore.is_real_fico_score,
                        uuid: this.activeScore.uuid,
                        user_id: this.activeScore.user_id,
                    };
                    this.mode = 'edit';
                } else {
                    this.scoreData = {
                        scoreDate: new Date(),
                        score: '',
                        isRealFicoScore: false,
                    };
                    this.mode = 'create';
                }
            }
        },
    },
    methods: {
        ...mapActions(useCreditScoreStore, [
            'saveCreditScore',
            'updateCreditScore',
            'getUserScores',
            'setActiveScore',
            'setUsers',
            'setActiveUser',
        ]),

        clearTimeout(timeoutId) {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        },

        submitCreditScore() {
            this.validate();

            if (this.errors.length === 0) {
                this.saveCreditScore(this.scoreData)
                    .then(() => {
                        // HubSpot Event Tracking for Submitted Credit Score
                        if (window._hsq) {
                            const appStore = useAppStore(); // Assumes import is present
                            const loggedInUser = appStore.userSettings;
                            let userFirstName = null;
                            let userLastName = null;

                            if (loggedInUser && loggedInUser.name) {
                                const nameParts = loggedInUser.name.split(' ');
                                userFirstName = nameParts[0];
                                if (nameParts.length > 1) {
                                    userLastName = nameParts.slice(1).join(' ');
                                }
                            }

                            const eventProperties = {
                                email: loggedInUser ? loggedInUser.email : null,
                                firstname: userFirstName,
                                lastname: userLastName,
                                my10x_user_uuid: loggedInUser
                                    ? loggedInUser.uuid
                                    : null,
                                credit_score_value: this.scoreData.score
                                    ? parseInt(this.scoreData.score)
                                    : null,
                                credit_score_source: this.scoreData
                                    .isRealFicoScore
                                    ? 'FICO'
                                    : 'Unknown', // Validated to be FICO
                                credit_score_date_pulled: this.scoreData
                                    .scoreDate
                                    ? new Date(this.scoreData.scoreDate)
                                          .toISOString()
                                          .split('T')[0]
                                    : null,
                            };

                            // Remove null/undefined properties
                            for (const key in eventProperties) {
                                if (
                                    eventProperties[key] === null ||
                                    eventProperties[key] === undefined
                                ) {
                                    delete eventProperties[key];
                                }
                            }

                            window._hsq.push([
                                'trackCustomBehavioralEvent',
                                {
                                    name: 'pe22692776_submitted_credit_score',
                                    properties: eventProperties,
                                },
                            ]);
                        }
                        // End HubSpot Event Tracking

                        this.getUserScores().then(() => {
                            this.closeModal();
                        });
                    })
                    .catch((error) => {
                        console.error('Error saving credit score:', error);
                    });
            }
        },
        updateScore() {
            this.validate();

            if (this.errors.length === 0) {
                this.updateCreditScore(this.scoreData)
                    .then(() => {
                        this.getUserScores().then(() => {
                            this.closeModal();
                        });
                    })
                    .catch((error) => {});
            }
        },
        checkScoreLimits() {
            const scoreMessage =
                'Please enter a score between<br />300 and 850';
            if (String(this.scoreData.score).length === 3) {
                if (
                    Math.floor(this.scoreData.score) >= 300 &&
                    Math.floor(this.scoreData.score) <= 850
                ) {
                    this.scoreMessage = null;
                }
                if (Math.floor(this.scoreData.score) < 300) {
                    this.scoreData.score = 300;
                    this.scoreMessage = scoreMessage;
                }
                if (Math.floor(this.scoreData.score) > 850) {
                    this.scoreData.score = 850;
                    this.scoreMessage = scoreMessage;
                }
            }
        },
        validate() {
            this.errors = [];
            this.scoreMessage = null;
            this.dateError = false;
            this.scoreError = false;
            this.ficoCheckError = false;

            if (!this.scoreData.scoreDate) {
                this.dateError = true;
                this.errors.push({
                    message: 'Select date',
                });
            }

            if (!this.scoreData.isRealFicoScore) {
                this.ficoCheckError = true;
                this.errors.push({
                    message: 'Confirm this is your real FICO score',
                });
            }

            if (!this.scoreData.score || Math.floor(this.scoreData.score) < 300) {
                this.scoreError = true;
                this.errors.push({
                    message: 'Enter your FICO score',
                });
            } else if (Math.floor(this.scoreData.score) > 850) {
                this.scoreError = true;
                this.errors.push({
                    message: 'The score cannot be more than 850.',
                });
            }
        },
        closeModal() {
            this.scoreData.scoreDate = '';
            this.scoreData.score = '';
            this.scoreData.isRealFicoScore = false;
            this.scoreData.uuid = null;
            this.errors = [];
            this.setActiveScore(null);
            this.mode = 'create';
            this.$emit('close');
        },
        toggleWhyMatter() {
            this.showWhyMatter = !this.showWhyMatter;
            if (this.showWhyMatter) {
                this.$nextTick(() => {
                    this.updatePopoverPosition();
                });
            }
        },

        showPopover() {
            this.showWhyMatter = true;
            this.updatePopoverPosition();
        },
        updatePopoverPosition() {
            this.$nextTick(() => {
                if (this.$refs.whyMatterLink) {
                    const linkRect = this.$refs.whyMatterLink.getBoundingClientRect();


                    this.popoverStyle = {
                        top: `${linkRect.bottom + window.scrollY + 10}px`,
                        left: `${linkRect.left + window.scrollX}px`,
                        maxWidth: '480px',
                        width: '480px',
                        zIndex: 9999,
                    };

                    const viewportWidth = window.innerWidth;
                    const popoverRight = linkRect.left + 480;

                    if (popoverRight > viewportWidth - 20) {
                        this.popoverStyle.left = `${Math.max(20, viewportWidth - 500)}px`;
                    }
                }
            });
        },
    },
    created() {},
    mounted() {
        window.addEventListener('resize', this.updatePopoverPosition);
        window.addEventListener('scroll', this.updatePopoverPosition);
        this.setUsers().then(() => {
            this.setActiveUser(this.users[0]);
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updatePopoverPosition);
        window.removeEventListener('scroll', this.updatePopoverPosition);
    },
};
</script>
